/**
 * Module dependencies.
 */

import { Carousel, Slide, Subtitle, Type } from 'src/components/core';
import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { TechnologyProps } from 'src/types/technologies';
import { detectMobile } from 'src/core/utils/devices';
import { ifNotProp, theme } from 'styled-tools';
import { media, units } from '@untile/react-components';
import Container from 'src/components/core/layout/container';
import ImageVideoSlide, { GalleryItem } from './image-video-slide';
import head from 'lodash/head';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import size from 'lodash/size';
import styled, { css } from 'styled-components';
import union from 'lodash/union';
import useTranslate from 'src/hooks/use-translate';

/**
 * `Props` type.
 */

type Props = Pick<
  TechnologyProps,
  'realUseCasesGallery' | 'realUseCasesVideoImage' | 'realUseCasesVideoUrl'
> & {
  className?: string;
};

/**
 * `Section` styled component.
 */

const Section = styled.section`
  position: relative;
`;

/**
 * `StyledContainer` styled component.
 */

const StyledContainer = styled(Container)`
  padding: 0;
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div<{ hasCarousel: boolean }>`
  display: grid;
  grid-template-areas:
    '.        titleWrapper .'
    'carousel carousel     carousel';
  grid-template-columns: ${theme('grid.gutterMobile')}px 1fr ${theme(
      'grid.gutterMobile'
    )}px;

  ${ifNotProp(
    'hasCarousel',
    css`
      grid-template-areas:
        '. titleWrapper .'
        '. carousel     .';
    `
  )}

  ${media.min('md')`
    grid-template-columns: 1fr 10fr 1fr;
  `}
`;

/**
 * `TitleWrapper` styled component.
 */

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  grid-area: titleWrapper;

  ${media.min('md')`
    text-align: center;
  `}
`;

/**
 * `GallerySubtitle` styled component.
 */

const GallerySubtitle = styled(Subtitle).attrs({ size: 'large' })`
  height: ${units(4)};
  margin-bottom: ${units(4)};

  ${media.min('md')`
    height: ${units(5.5)};
    margin-bottom: ${units(6)};
  `}
`;

/**
 * `CarouselWrapper` styled component.
 */

const CarouselWrapper = styled.div`
  grid-area: carousel;
  margin-bottom: ${units(2.5)};
  position: relative;

  ${media.min('md')`
    margin-bottom: ${units(1)};
  `}
`;

/**
 * Carousel config.
 */

const carouselConfig = {
  breakpoints: {
    768: {
      slidesPerView: 1.1,
      spaceBetween: 30
    },
    1600: {
      slidesPerView: 1.6,
      spaceBetween: 40
    }
  }
};

/**
 * `RealUsageCasesSection` component.
 */

const RealUsageCasesSection = (props: Props): ReactElement => {
  const {
    className,
    realUseCasesGallery,
    realUseCasesVideoImage,
    realUseCasesVideoUrl
  } = props;

  const animProps = {
    'data-scroll': 'fade-in-up',
    'data-scroll-offset': '10%'
  };

  const [isMobileDevice, setIsMobileDevice] = useState<boolean>();
  const [activeSlide, setActiveSlide] = useState<number>(0);
  const { translate } = useTranslate();
  const sliderRef = useRef<any>();
  const gallery = useMemo<GalleryItem[]>(() => {
    let video = [];

    if (realUseCasesVideoImage && realUseCasesVideoUrl) {
      video = [
        {
          coverImage: head(realUseCasesVideoImage).url,
          label: head(realUseCasesVideoImage).subtitle,
          type: 'video',
          url: realUseCasesVideoUrl
        }
      ];
    }

    const imagesGallery = map(realUseCasesGallery, ({ subtitle, url }) => ({
      label: subtitle,
      type: 'image',
      url
    }));

    return union(video, imagesGallery);
  }, [realUseCasesGallery, realUseCasesVideoImage, realUseCasesVideoUrl]);

  useEffect(() => {
    if (sliderRef && sliderRef.current) {
      sliderRef.current.swiper.autoplay.start();
    }
  }, []);

  useEffect(() => {
    setIsMobileDevice(detectMobile());
  }, []);

  if (size(gallery) === 0) {
    return null;
  }

  return (
    <Section className={className}>
      <StyledContainer>
        <Grid hasCarousel={size(gallery) > 1}>
          <TitleWrapper>
            <Type.H2 {...animProps}>
              {translate('technologies:realUsageCases')}
            </Type.H2>

            <GallerySubtitle {...animProps}>
              {gallery[activeSlide ?? 0]?.label ?? ''}
            </GallerySubtitle>
          </TitleWrapper>

          <CarouselWrapper
            data-scroll={'fade-in-up-small'}
            data-scroll-offset={'20%'}
          >
            {size(gallery) > 1 ? (
              <Carousel
                activeSlide={activeSlide}
                autoplay={{
                  delay: 10000,
                  disableOnInteraction: true
                }}
                carouselConfig={carouselConfig}
                containerModifierClass={'real-usage-cases'}
                onSetActiveSlide={setActiveSlide}
                ref={sliderRef}
                totalItems={size(gallery)}
              >
                {map(gallery, (props, index) => (
                  <Slide key={index}>
                    <ImageVideoSlide
                      hasSiblings={!isEmpty(realUseCasesGallery)}
                      isMobileDevice={isMobileDevice}
                      {...props}
                    />
                  </Slide>
                ))}
              </Carousel>
            ) : (
              <ImageVideoSlide
                isCarouselSlide={false}
                isMobileDevice={isMobileDevice}
                {...head(gallery)}
              />
            )}
          </CarouselWrapper>
        </Grid>
      </StyledContainer>
    </Section>
  );
};

/**
 * Export `RealUsageCasesSection` component.
 */

export default RealUsageCasesSection;
